/**
 * API Contract Designer Storage
 * IndexedDB persistence for API contracts
 */

import type { APIContract } from './types';

const DB_NAME = 'api-contract-db';
const DB_VERSION = 1;
const STORE_NAME = 'contracts';

/**
 * Initialize the database
 */
export async function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create contracts store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };
  });
}

/**
 * Generate a unique ID
 */
function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a new contract
 */
export async function createContract(name: string): Promise<APIContract> {
  const db = await initDB();
  const now = new Date().toISOString();

  const contract: APIContract = {
    id: generateId(),
    name,
    version: '1.0.0',
    description: '',
    servers: [
      {
        id: generateId(),
        url: 'https://api.example.com/v1',
        description: 'Production server',
      },
    ],
    endpoints: [],
    schemas: [],
    securitySchemes: [],
    tags: [],
    versions: [],
    consumers: [],
    createdAt: now,
    updatedAt: now,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.add(contract);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(contract);
  });
}

/**
 * Save/update a contract
 */
export async function saveContract(contract: APIContract): Promise<void> {
  const db = await initDB();
  const updatedContract = {
    ...contract,
    updatedAt: new Date().toISOString(),
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(updatedContract);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Load a contract by ID
 */
export async function loadContract(id: string): Promise<APIContract | null> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}

/**
 * List all contracts
 */
export async function listContracts(): Promise<APIContract[]> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('updatedAt');
    const request = index.openCursor(null, 'prev');
    const contracts: APIContract[] = [];

    request.onerror = () => reject(request.error);
    request.onsuccess = (event) => {
      const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result;
      if (cursor) {
        contracts.push(cursor.value);
        cursor.continue();
      } else {
        resolve(contracts);
      }
    };
  });
}

/**
 * Delete a contract
 */
export async function deleteContract(id: string): Promise<void> {
  const db = await initDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

/**
 * Duplicate a contract
 */
export async function duplicateContract(id: string, newName: string): Promise<APIContract> {
  const original = await loadContract(id);
  if (!original) {
    throw new Error('Contract not found');
  }

  const now = new Date().toISOString();
  const duplicated: APIContract = {
    ...original,
    id: generateId(),
    name: newName,
    versions: [],  // Reset version history
    createdAt: now,
    updatedAt: now,
  };

  await saveContract(duplicated);
  return duplicated;
}

/**
 * Save a version snapshot
 */
export async function saveVersion(
  contractId: string,
  version: string,
  notes?: string
): Promise<void> {
  const contract = await loadContract(contractId);
  if (!contract) {
    throw new Error('Contract not found');
  }

  const versionSnapshot = {
    id: generateId(),
    version,
    createdAt: new Date().toISOString(),
    snapshot: JSON.stringify({
      ...contract,
      versions: [],  // Don't include version history in snapshot
    }),
    notes,
  };

  contract.versions.push(versionSnapshot);
  contract.version = version;
  await saveContract(contract);
}

/**
 * Export contract to JSON
 */
export function exportToJSON(contract: APIContract): string {
  return JSON.stringify(contract, null, 2);
}

/**
 * Import contract from JSON
 */
export async function importFromJSON(json: string): Promise<APIContract> {
  const parsed = JSON.parse(json) as APIContract;
  const now = new Date().toISOString();

  const contract: APIContract = {
    ...parsed,
    id: generateId(),
    createdAt: now,
    updatedAt: now,
    versions: [],  // Reset version history on import
  };

  await saveContract(contract);
  return contract;
}
